import React from 'react';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { GEAR_CATEGORIES } from '../constants/gearCategories';

/**
 * Per-category gear editor: [ ✓ ]  Category label  [ notes input ].
 * Notes are only enabled while the category is checked.
 *
 * value:    array of { category, brings, notes }
 * onChange: receives the next array (pruned of empty/unchecked rows)
 */
export default function GearEditor({ value = [], onChange, disabled = false }) {
  const byCat = React.useMemo(() => {
    const m = {};
    (value || []).forEach((g) => { if (g && g.category) m[g.category] = g; });
    return m;
  }, [value]);

  // Rebuild the sparse list from the working map; drop rows that carry nothing.
  const emit = (map) => {
    const next = GEAR_CATEGORIES
      .map(({ value: cat }) => map[cat])
      .filter((g) => g && (g.brings || (g.notes && g.notes.trim())))
      .map((g) => ({ category: g.category, brings: !!g.brings, notes: g.notes ? g.notes.trim() : null }));
    onChange(next);
  };

  const toggle = (cat) => {
    const cur = byCat[cat] || { category: cat, brings: false, notes: null };
    emit({ ...byCat, [cat]: { ...cur, category: cat, brings: !cur.brings } });
  };

  const setNotes = (cat, notes) => {
    const cur = byCat[cat] || { category: cat, brings: true, notes: null };
    emit({ ...byCat, [cat]: { ...cur, category: cat, notes } });
  };

  return (
    <div className="gear-editor space-y-2">
      {GEAR_CATEGORIES.map(({ value: cat, label }) => {
        const item = byCat[cat];
        const brings = !!(item && item.brings);
        return (
          <div key={cat} className="flex items-center gap-3">
            <Checkbox
              id={`gear-${cat}`}
              checked={brings}
              onCheckedChange={() => toggle(cat)}
              disabled={disabled}
              data-testid={`gear-check-${cat}`}
            />
            <label
              htmlFor={`gear-${cat}`}
              className="shrink-0 cursor-pointer select-none"
              style={{
                fontFamily: 'var(--s8-font-mono)',
                fontSize: '12px',
                letterSpacing: '0.02em',
                width: '128px',
                color: brings ? 'var(--green-ok, #5A9A6B)' : 'var(--s8-text-muted)',
              }}
            >
              {label}
            </label>
            <Input
              value={(item && item.notes) || ''}
              onChange={(e) => setNotes(cat, e.target.value)}
              disabled={disabled || !brings}
              maxLength={200}
              placeholder={brings ? 'e.g. Fender Twin Reverb' : ''}
              className="h-9 bg-secondary border-border flex-1 disabled:opacity-40"
              data-testid={`gear-notes-${cat}`}
            />
          </div>
        );
      })}
    </div>
  );
}
